import { api } from '@/lib/api-client'
import { endOfDayParam, operationsNow, startOfDayParam, toDateInput } from '@/lib/datetime'
import type { BookingReport } from '@/types/api'

export const REPORT_RANGE_KEYS = ['7d', '30d', '90d', 'all'] as const
export type ReportRangeKey = (typeof REPORT_RANGE_KEYS)[number]

/** Thirty days is the operational default — a month of trading at a glance. */
export const DEFAULT_REPORT_RANGE: ReportRangeKey = '30d'

interface RangeMeta {
  key: ReportRangeKey
  label: string
  /** Compact label for the segmented control. */
  shortLabel: string
  /** Window length in days, or null for all-time. */
  days: number | null
}

export const REPORT_RANGES: Record<ReportRangeKey, RangeMeta> = {
  '7d': { key: '7d', label: 'Last 7 days', shortLabel: '7D', days: 7 },
  '30d': { key: '30d', label: 'Last 30 days', shortLabel: '30D', days: 30 },
  '90d': { key: '90d', label: 'Last 90 days', shortLabel: '90D', days: 90 },
  all: { key: 'all', label: 'All time', shortLabel: 'All', days: null },
}

export function isReportRangeKey(value: string | null | undefined): value is ReportRangeKey {
  return value != null && (REPORT_RANGE_KEYS as readonly string[]).includes(value)
}

const pad = (value: number): string => String(value).padStart(2, '0')

/**
 * Turns a preset into the `from`/`to` the API takes. All-time sends neither
 * bound, so the server reports across the whole dataset.
 *
 * The window is figured on the Cairo operational date — the same clock the
 * backend counts against — and on the calendar date alone, so a DST change
 * cannot shift a boundary by an hour.
 */
export function rangeQuery(key: ReportRangeKey): { from?: string; to?: string } {
  const meta = REPORT_RANGES[key]
  if (meta.days == null) return {}

  const now = operationsNow()
  const todayIso = toDateInput(now)
  const start = new Date(Date.UTC(now.year, now.month - 1, now.day))
  start.setUTCDate(start.getUTCDate() - (meta.days - 1))
  const fromIso = `${start.getUTCFullYear()}-${pad(start.getUTCMonth() + 1)}-${pad(start.getUTCDate())}`

  return { from: startOfDayParam(fromIso), to: endOfDayParam(todayIso) }
}

export function fetchBookingReport(key: ReportRangeKey): Promise<BookingReport> {
  return api.get<BookingReport>('/api/admin/reports', { query: rangeQuery(key) })
}
